import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaTrash, FaDownload, FaEnvelope, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { subscriberService } from '../../services/api';
import toast from 'react-hot-toast';

const SubscriberManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive

  useEffect(() => {
    loadSubscribers();
  }, [filter]);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const response = await subscriberService.getAllSubscribers(filter);
      setSubscribers(response.data.subscribers);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load subscribers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, email) => {
    if (window.confirm(`Are you sure you want to delete subscriber "${email}"?`)) {
      try {
        await subscriberService.deleteSubscriber(id);
        toast.success('Subscriber deleted successfully');
        loadSubscribers();
      } catch (error) {
        toast.error('Failed to delete subscriber');
        console.error(error);
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await subscriberService.exportSubscribers();
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Subscribers exported successfully');
    } catch (error) {
      toast.error('Failed to export subscribers');
      console.error(error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4>
          <FaEnvelope className="me-2" />
          Newsletter Subscribers
        </h4>
        <Button variant="success" onClick={handleExport}>
          <FaDownload className="me-2" />
          Export CSV
        </Button>
      </Card.Header>
      <Card.Body>
        {/* Stats Row */}
        <div className="row mb-4">
          <div className="col-md-4">
            <Card className="text-center" style={{ borderColor: '#0066cc' }}>
              <Card.Body>
                <h2 className="mb-0" style={{ color: '#0066cc' }}>{stats.total}</h2>
                <p className="text-muted mb-0">Total Subscribers</p>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-4">
            <Card className="text-center" style={{ borderColor: '#28a745' }}>
              <Card.Body>
                <h2 className="mb-0" style={{ color: '#28a745' }}>{stats.active}</h2>
                <p className="text-muted mb-0">Active</p>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-4">
            <Card className="text-center" style={{ borderColor: '#dc3545' }}>
              <Card.Body>
                <h2 className="mb-0" style={{ color: '#dc3545' }}>{stats.inactive}</h2>
                <p className="text-muted mb-0">Unsubscribed</p>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="mb-3">
          <Button
            variant={filter === 'all' ? 'primary' : 'outline-primary'}
            size="sm"
            className="me-2"
            onClick={() => setFilter('all')}
          >
            All ({stats.total})
          </Button>
          <Button
            variant={filter === 'active' ? 'success' : 'outline-success'}
            size="sm"
            className="me-2"
            onClick={() => setFilter('active')}
          >
            Active ({stats.active})
          </Button>
          <Button
            variant={filter === 'inactive' ? 'danger' : 'outline-danger'}
            size="sm"
            onClick={() => setFilter('inactive')}
          >
            Unsubscribed ({stats.inactive})
          </Button>
        </div>

        {/* Subscribers Table */}
        {subscribers.length === 0 ? (
          <Alert variant="info">
            No subscribers yet. Newsletter signup forms are available on the home page and blog.
          </Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Subscribed</th>
                <th>Last Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((subscriber) => (
                <tr key={subscriber._id}>
                  <td>
                    <strong>{subscriber.email}</strong>
                  </td>
                  <td>{subscriber.name}</td>
                  <td>
                    {subscriber.active ? (
                      <Badge bg="success">
                        <FaCheckCircle className="me-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge bg="danger">
                        <FaTimesCircle className="me-1" />
                        Unsubscribed
                      </Badge>
                    )}
                  </td>
                  <td>{formatDate(subscriber.subscribedAt)}</td>
                  <td>{subscriber.lastEmailSent ? formatDate(subscriber.lastEmailSent) : 'Never'}</td>
                  <td>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(subscriber._id, subscriber.email)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default SubscriberManagement;
